import React, { useCallback, useMemo, useRef, useState } from "react";
import { Button, CheckBoxInput, InputField } from "@/components";
import { Paperclip, ArrowRight, Video } from "lucide-react";
import { GoArrowLeft } from "react-icons/go";
import { useNavigate, useParams } from "react-router-dom";
import { RightWrapper } from "../../../components";
import { useDispatch, useSelector } from "react-redux";
import { selectEscalationById, replyEscalation } from "../../../app/Slices";
import { useApi, useFormData, useAlert } from "@/";
import {
  getEscalationTypeLabel,
  getSubcategoryLabel,
} from "../../../utils/Data";
import { PhotoClipView } from "@/components";
const RemarkCard = ({
  sender,
  timestamp,
  message,
  remarkNo,
  isRead,
  attachments,
}) => {
  const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "avi", "mkv", "wmv", "flv", "m4v", "3gp", "mpeg", "mpg", "ts", "vob", "ogv", "mts", "m2ts"];
  const pdf = attachments.filter((file) => file.fileType === "pdf");
  const videos = attachments.filter((file) => VIDEO_EXTENSIONS.includes(file.fileType?.toLowerCase()));
  const images = attachments
    .filter((file) => file.fileType !== "pdf" && !VIDEO_EXTENSIONS.includes(file.fileType?.toLowerCase()))
    .map((file) => file.url);
  return (
    <div className="px-4 py-3 bg-background rounded-[20px] shadow-sm">
      <div className="flex justify-between items-center text-sm font-medium">
        <div className={`w-full flex-1 flex items-center gap-2`}>
          <span className="text-secondary/90 font-bold font-nunito text-body1">
            {sender}
          </span>
          <span className="text-secondary/50 text-xs">{timestamp}</span>
        </div>
        {remarkNo && (
          <span className="text-sm text-secondary/40 font-bold font-nunito">
            Remark {remarkNo}
          </span>
        )}
      </div>
      <p className="mt-2 text-secondary/85 text-body2 leading-5">{message}</p>
      {attachments?.length > 0 && (
        <>
          {pdf?.length > 0 && (
            <div className="flex flex-col items-start mt-1">
              {pdf.map((file) => (
                <a
                  key={file._id}
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-secondary/90 flex group transition-all items-center gap-0.5 font-medium font-nunito underline !text-blue-500/80 hover:text-blue-500 text-caption"
                >
                  <Paperclip className="w-2.5 h-2.5 -mb-0.5 text-secondary/80 group-hover:text-blue-500" />{" "}
                  {file.fileName}
                </a>
              ))}
            </div>
          )}
          {videos?.length > 0 && (
            <div className="flex flex-col items-start mt-1">
              {videos.map((file) => (
                <a
                  key={file._id}
                  href={file.url}
                  download={file.fileName}
                  className="text-secondary/90 flex group transition-all items-center gap-0.5 font-medium font-nunito underline !text-blue-500/80 hover:text-blue-500 text-caption"
                >
                  <Video className="w-2.5 h-2.5 -mb-0.5 text-secondary/80 group-hover:text-blue-500" />{" "}
                  {file.fileName}
                </a>
              ))}
            </div>
          )}
          {images?.length > 0 && (
            <div className="mt-2 flex items-center gap-1">
              <PhotoClipView images={images} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const InfoCard = ({ label, value, index, totalItems }) => {
  const roundedClass = `
    ${index === 0 ? "rounded-tl-2xl" : ""} 
    ${index === totalItems - 1 && totalItems % 2 === 0 ? "rounded-br-2xl" : ""} 
    ${index === 1 ? "rounded-tr-2xl" : ""} 
    ${index === totalItems - 2 && totalItems % 2 === 0 ? "rounded-bl-2xl" : ""}
  `;

  return (
    <div
      className={`bg-background/5 px-2.5 py-1.5 rounded flex flex-col gap-0.5 overflow-hidden line-clamp-1 ${roundedClass}`}
    >
      <h6 className="!text-xs font-semibold text-background/65 font-nunito">
        {label}
      </h6>
      <h2 className="text-background/95 text-body1">{value}</h2>
    </div>
  );
};

const InfoSection = ({ title, data }) => (
  <div className="mb-4 flex flex-col gap-1">
    <h3 className="text-xl font-semibold ml-1 text-background/95 font-nunito">
      {title}
    </h3>
    <div className="bg-background/5 p-0.5 rounded-2xl shadow-sm mb-2 grid grid-cols-2 gap-0.5">
      {data.map(([label, value], index) => (
        <InfoCard
          key={index}
          label={label}
          value={value}
          index={index}
          totalItems={data.length}
        />
      ))}
    </div>
  </div>
);

const Remarks = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { alert, AlertDialog } = useAlert();
  const dispatch = useDispatch();
  const { callApi } = useApi();
  const convertToFormData = useFormData();
  const escalation =
    useSelector((state) => selectEscalationById(state, id)) || {};
  console.log("EscalationData:::::", escalation);

  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }, []);

  let count = 0;
  const remarks =
    escalation?.chat?.map((c) => {
      return {
        id: c?._id,
        sender:
          c?.metadata?.userRole === "customer"
            ? "Customer"
            : "Indian Courier Company",
        timestamp: formatDate(c?.createdAt) || "--:--",
        message: c?.content,
        remarkNo: c?.metadata?.userRole === "customer" ? ++count : "",
        attachments: c?.attachments || [],
        isRead: c?.isRead,
      };
    }) || [];

  const escalationInfo = (data) => [
    ["Escalation ID", data?.escalationId],
    ["Escalation Status", data?.status],
    ["Customer Name", data?.user?.name || "N/A"],
    ["Customer ID", data?.user?.clientId || "N/A"],
    [
      "Total Remarks",
      data?.chat?.filter((c) => c?.metadata?.userRole === "customer").length,
    ],
    ["Total Shipment", data?.shipments?.length],
    ["Escalation Type", getEscalationTypeLabel(data?.escalationType)],
    ["Escalation SubType", getSubcategoryLabel(data?.escalationSubType)],
    ["AWB", data?.shipments?.map((s) => s?.awbNumber).join(", ") || "N/A"],
    [
      "Shipping ID",
      data?.shipments?.map((s) => s?.shippingId).join(", ") || "N/A",
    ],
    ["Order ID", data?.shipments?.map((s) => s?.orderId).join(", ") || "N/A"],
    [
      "Escalation Date",
      data?.escalationDate ? formatDate(data?.escalationDate) : "",
    ],
    [
      "Last Remark Date",
      data?.chat && data?.chat?.length > 0
        ? formatDate(data?.chat[data?.chat?.length - 1]?.createdAt)
        : "",
    ],
  ];

  const weightDisputeInfo = (data) => {
    const wd = data?.shipments?.[0]?.weightDetails;
    if (!wd) return [];
    const dims = wd.enteredDimensions;
    const dimStr = dims
      ? `${dims.length || 0} x ${dims.breadth || 0} x ${dims.height || 0} cm`
      : "N/A";
    return [
      [
        "Booking Weight",
        wd.enteredWeight != null ? `${wd.enteredWeight} kg` : "N/A",
      ],
      ["Dimensions (LxBxH)", dimStr],
      [
        "Charged Weight",
        wd.appliedWeight != null ? `${wd.appliedWeight} kg` : "N/A",
      ],
      [
        "Volumetric Weight",
        wd.volumetricWeight != null ? `${wd.volumetricWeight} kg` : "N/A",
      ],
    ];
  };

  const fileInputRef = useRef(null);
  const [selectedFileNames, setSelectedFileNames] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [isClose, setIsClose] = useState(false);
  const [finalWeight, setFinalWeight] = useState("");

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const ALLOWED_VIDEO_EXTENSIONS = [".mp4", ".mov", ".webm"];
    const VIDEO_MAX_SIZE = 10 * 1024 * 1024; // 10MB
    const IMAGE_PDF_MAX_SIZE = 5 * 1024 * 1024; // 5MB

    const isVideoFile = (file) => {
      if (file.type && file.type.startsWith("video/")) return true;
      const ext = "." + file.name.split(".").pop().toLowerCase();
      return ALLOWED_VIDEO_EXTENSIONS.includes(ext);
    };

    const files = Array.from(event.target.files);
    if (files.length > 0) {
      // Validate each file
      for (const file of files) {
        if (isVideoFile(file)) {
          const ext = "." + file.name.split(".").pop().toLowerCase();
          if (!ALLOWED_VIDEO_EXTENSIONS.includes(ext)) {
            alert(`Unsupported video format. Only MP4, MOV and WebM are allowed.`);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }
          if (file.size > VIDEO_MAX_SIZE) {
            alert(`Video "${file.name}" exceeds the 10MB limit.`);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }
        } else {
          if (file.size > IMAGE_PDF_MAX_SIZE) {
            alert(`File "${file.name}" exceeds the 5MB limit.`);
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
          }
        }
      }

      const fileNames = files.map((file) => file.name).join(", ");
      setSelectedFileNames(fileNames);
      setAttachments(files);
    }
  };

  const handleSubmit = async () => {
    if (!message) {
      alert("Please enter a message");
      return;
    }

    if (
      escalation.escalationType === "weightDispute" &&
      isClose &&
      !finalWeight
    ) {
      alert("Please enter final weight");
      return;
    }

    const data = {
      data: {
        message,
        markAsClosed: isClose,
        ...(escalation.escalationType === "weightDispute"
          ? { finalWeight: finalWeight }
          : {}),
      },
      ...(attachments
        ? attachments.reduce(
            (acc, file, index) => ({
              ...acc,
              [`proof${index + 1}`]: file,
            }),
            {},
          )
        : {}),
    };
    const formData = convertToFormData(data);
    console.log("Form data:", formData);

    try {
      const response = await dispatch(
        replyEscalation({
          data: formData,
          escalationId: escalation?._id,
        }),
      );
      if (replyEscalation.fulfilled.match(response)) {
        setAttachments([]);
        setSelectedFileNames("");
        setMessage("");
        setFinalWeight("");
        setIsClose(false);
        console.log("Reply sent successfully");
        alert("Reply sent successfully");
      } else if (replyEscalation.rejected.match(response)) {
        alert("Failed to send reply");
        console.error("Failed to send reply");
      }

      console.log("Response:", response);
    } catch (error) {
      console.log(error);
    }
  };

  const handleViewShipments = () => {
    const awbs = escalation?.shipments?.map((s) => s?.awbNumber);
    console.log(awbs);
    navigate(`/tracking?awbs=${awbs?.join(",")}`);
  };

  return (
    <RightWrapper>
      <div className="w-full h-full flex-1 flex flex-col gap-2 p-4 pb-16 md:pb-0 relative max-w-[2000px]">
        {/* Left Section: Remarks */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} type="button">
            <GoArrowLeft className="text-4xl lg:text-5xl hover:bg-secondary/10 rounded-lg p-1 lg:p-1.5 text-secondary/95" />
          </button>
          <h2 className="text-xl lg:text-3xl font-semibold text-secondary/95 font-nunito my-2">
            Escalation #{escalation?.escalationId}
          </h2>
        </div>
        <div className="w-full min-h-[780px] flex flex-col lg:flex-row gap-4 p-2">
          <div className="flex  flex-col gap-2 w-full lg:w-3/5">
            <div className="flex-1  bg-background_2 flex flex-col p-1.5 rounded-3xl shadow">
              <div className="space-y-1.5 mb-6 flex-1 w-full">
                {remarks.map((remark, index) => (
                  <RemarkCard key={index} {...remark} />
                ))}
              </div>
              <div className="px-3 py-2 bg-secondary/5 rounded-[20px] shadow-sm border border-secondary/20 flex-grow-0 flex-shrink-0">
                <div className="mb-2 flex flex-col md:flex-row w-full justify-between gap-2">
                  <h3 className="text-body1 font-nunito font-semibold pl-1 flex-1 text-secondary">
                    Add New Remarks
                  </h3>
                  <CheckBoxInput
                    label="Mark as Close"
                    checked={isClose}
                    onChange={() => setIsClose(!isClose)}
                    disabled={escalation?.status === "Resolved"}
                    className="!w-fit mr-1"
                  />
                </div>
                {escalation?.escalationType === "weightDispute" && isClose && (
                  <InputField
                    type="number"
                    label="Final Weight (in kg)"
                    placeholder="Enter final weight"
                    value={finalWeight}
                    onChange={setFinalWeight}
                    disabled={escalation?.status === "Resolved"}
                    className="mb-2 !bg-background"
                  />
                )}
                <textarea
                  placeholder="Add your message here"
                  className="w-full p-3 border border-secondary/20 rounded-xl text-secondary/90 placeholder:text-secondary/40 bg-background mb-2 focus:outline-none focus:ring-2 focus:ring-secondary/40"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={escalation?.status === "Resolved"}
                ></textarea>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    id={"attachments"}
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept={".pdf, image/png, image/jpeg, video/mp4, video/quicktime, video/webm, .mp4, .mov, .webm"}
                    style={{ display: "none" }}
                    multiple={true}
                    disabled={escalation?.status === "Resolved"}
                  />
                  <Button
                    variant="outline"
                    className="flex items-center gap-2 !border-[1.6px] border-dashed !bg-secondary/80 !text-background !border-background/30"
                    onClick={handleButtonClick}
                    disabled={escalation?.status === "Resolved"}
                  >
                    <Paperclip size={16} /> Browse
                  </Button>
                  <span className="text-secondary/60 line-clamp-2 text-sm">
                    {selectedFileNames || "No file Selected"}
                  </span>
                  <span className="text-secondary/40 text-xs hidden md:inline">
                    PNG, JPEG, PDF (max 5MB) | MP4, MOV, WebM (max 10MB)
                  </span>
                  <Button
                    className="ml-auto px-6 text-background/95"
                    onClick={handleSubmit}
                    disabled={escalation?.status === "Resolved"}
                  >
                    Send Remark
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section: Shipment & Escalation Info */}
          {escalation && (
            <div className="w-full lg:w-2/5 h-fit bg-secondary p-4 rounded-3xl shadow">
              <InfoSection
                title="Escalation Info"
                data={escalationInfo(escalation)}
              />
              {escalation?.escalationType === "weightDispute" && (
                <InfoSection
                  title="Weight Details"
                  data={weightDisputeInfo(escalation)}
                />
              )}
              <Button
                className="flex items-center w-full mt-4 hover:!bg-background/10"
                onClick={() => handleViewShipments()}
              >
                View Shipments
                <ArrowRight size={17} className="mt-0.5" />
              </Button>
            </div>
          )}
        </div>
      </div>
      <AlertDialog />
    </RightWrapper>
  );
};

export default Remarks;
